import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AppProvider } from 'foundations-components/components/AppProvider';
import { track } from '../_core/common/eventTracking/tracker';
import { useUPG6101Experiment, UPG_6101_TREATMENT_KEY } from '../experiments/useUpg6101Experiment';
import { logExposure } from '../experiments/laboratoryClient';
import { logRavenException } from '../_core/utils/errors';
import { writeUpgradeConfigToLocalStorage } from '../_core/utils/upgradeConfigCache';
import { openUpgradePageInNewTab } from '../_core/utils/upgradePageRedirect';
import { UpgradePointTooltip } from './UpgradePointTooltip';
import useErrorAlert from './useErrorAlert';
import { useUpgradePointTooltipData } from './useUpgradePointTooltipData';
import { VALUE_PROP_MODAL_CONFIGS } from './ValuePropModal/constants';
import { getUpgradeConfigTrackingProperties, getUpgradePointPromise, updateViewedUpgradePointsCache, warnArchivedUpgradePointUse } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
const ValuePropModalContent = /*#__PURE__*/lazy(() => import('./ValuePropModal/ValuePropModalContent'));

/**
 * ## Upgrade Management System
 *
 * `useUpgradePoint` takes your upgrade key as an argument and returns a callback
 * to open the associated upgrade page. Upgrade keys can be created and managed in the
 * [Upgrade Management System](<https://tools.hubteamqa.com/ums>) (`go/ums`).
 *
 * @example
 * ```tsx
 * const YourComponent = () => {
 *  const { openUpgradePage } = useUpgradePoint('your-upgrade-key');
 *
 *  return <Button onClick={openUpgradePage} />;
 * }
 * ```
 */
export function useUpgradePoint(upgradeKey, isFeatureLocked = true) {
  const {
    isUPG6101Variation,
    isUPG6101Fetched
  } = useUPG6101Experiment(upgradeKey);
  const {
    setShowErrorAlert
  } = useErrorAlert();
  const upgradePointPromise = useMemo(() => {
    return getUpgradePointPromise({
      upgradeKey
    });
  }, [upgradeKey]);
  const resolvedConfigRef = useRef(null);

  // Fetch upgrade config and track view
  useEffect(() => {
    resolvedConfigRef.current = null;
    if (isFeatureLocked && upgradeKey) {
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) {
          return;
        }
        resolvedConfigRef.current = upgradeConfig;
        updateViewedUpgradePointsCache(upgradeKey, () => {
          track('interaction', Object.assign({
            action: 'viewed upgrade point'
          }, getUpgradeConfigTrackingProperties(upgradeConfig)));
          if (upgradeConfig.status === 'ARCHIVED') {
            warnArchivedUpgradePointUse(upgradeConfig.upgradeKey);
          }
        });
      }).catch(err => {
        console.error(err);
      });
    }
  }, [isFeatureLocked, upgradeKey, upgradePointPromise]);

  // Track click and set state
  const openUpgradePage = useCallback(() => {
    try {
      if (!isFeatureLocked) {
        return;
      }
      if (resolvedConfigRef.current) {
        writeUpgradeConfigToLocalStorage(resolvedConfigRef.current);
      }
      openUpgradePageInNewTab(upgradeKey);
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) return;
        track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeConfigTrackingProperties(upgradeConfig)));
      }).catch(__error => {
        setShowErrorAlert(true);
      });
    } catch (error) {
      setShowErrorAlert(true);
      logRavenException({
        customMessage: 'useUpgradePoint tryCatch error: An error occured in defining openUpgradePage',
        error,
        uniqueId: upgradeKey
      });
    }
  }, [isFeatureLocked, setShowErrorAlert, upgradeKey, upgradePointPromise]);
  const UpgradePointTooltipWithUpgradeKey = useMemo(() => ({
    children
  }) => /*#__PURE__*/_jsx(UpgradePointTooltip, {
    upgradeKey: upgradeKey,
    enableTooltip: isFeatureLocked,
    children: children
  }), [upgradeKey, isFeatureLocked]);
  const upgradePointTooltipData = useUpgradePointTooltipData(upgradeKey, isFeatureLocked);
  const [isValuePropModalOpen, setIsValuePropModalOpen] = useState(false);
  const valuePropModalConfig = VALUE_PROP_MODAL_CONFIGS[upgradeKey];
  const modalContainerRef = useRef(document.createElement('div'));
  const openValuePropModal = useCallback(() => {
    if (!isUPG6101Fetched) {
      return;
    }
    if (valuePropModalConfig) {
      logExposure(UPG_6101_TREATMENT_KEY);
    }
    if (valuePropModalConfig && isUPG6101Variation) {
      upgradePointPromise.then(upgradeConfig => {
        if (!upgradeConfig) return;
        track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeConfigTrackingProperties(upgradeConfig)));
      }).catch(() => {});
      setIsValuePropModalOpen(true);
    } else {
      openUpgradePage();
    }
  }, [valuePropModalConfig, isUPG6101Fetched, isUPG6101Variation, openUpgradePage, upgradePointPromise]);
  useEffect(() => {
    if (isValuePropModalOpen && valuePropModalConfig) {
      const container = modalContainerRef.current;
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render( /*#__PURE__*/_jsx(AppProvider, {
        children: /*#__PURE__*/_jsx(Suspense, {
          fallback: null,
          children: /*#__PURE__*/_jsx(ValuePropModalContent, {
            config: valuePropModalConfig,
            onClose: () => setIsValuePropModalOpen(false)
          })
        })
      }));
      return () => {
        // Defer so we don't unmount the root while React is still rendering
        // (e.g. when onClose sets state that triggers this cleanup).
        void Promise.resolve().then(() => {
          root.unmount();
          document.body.removeChild(container);
        });
      };
    }
    return () => {};
  }, [isValuePropModalOpen, valuePropModalConfig]);
  return {
    openUpgradePage,
    openValuePropModal,
    UpgradePointTooltip: UpgradePointTooltipWithUpgradeKey,
    ValuePropModal: () => null,
    upgradePointTooltipData
  };
}